import { Box, Button, Grid, makeStyles } from "@material-ui/core";
import { Form, Formik, FormikProps, FormikTouched, FormikValues } from "formik";
import { FC, ReactNode, SVGProps } from "react";
import { useSharedStyles } from "../../common/sharedStyles";
import { ScreenProps } from "../../common/types";
import { ReactComponent as LogoImage } from "../../images/nz-funds-nz-logo.svg";
import { useAppSelector } from "../../store";
import GridLayout, { Actions, Body, Header, Row } from "../client/GridLayout";
import { AlertMessage, Colors, ErrorFocus, SubtitleText, TitleText } from "../controls/helpers";

interface ScaffoldAppScreenProps<Values> extends Omit<ScreenProps, "nextRoute"> {
  children?: ((props: FormikProps<Values>) => ReactNode) | ReactNode;
  HeaderIcon?: FC<SVGProps<SVGSVGElement>>;
  initialTouched?: FormikTouched<Values>;
  initialValues: Values;
  validationSchema?: any;
  onSubmit: (values: Values) => void;
  hidePrev?: boolean;
  hideNext?: boolean;
  nextRouteHref?: string;
  disablePrev?: boolean;
  disableNext?: boolean;
  nextLabel?: string;
  submitFailed?: boolean;
  errorMessage?: string;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  isNarrowView?: boolean;
}

const ScaffoldAppScreen = <Values extends FormikValues>(props: ScaffoldAppScreenProps<Values>) => {
  const { isLoading, isSubmitting } = useAppSelector((state) => state.clientApplication);
  const classes = useStyles();
  const sharedClasses = useSharedStyles();
  const {
    children,
    disablePrev,
    disableNext,
    errorMessage,
    header,
    HeaderIcon,
    hideNext,
    hidePrev,
    initialTouched,
    initialValues,
    isNarrowView,
    nextLabel,
    nextRouteHref,
    onSubmit,
    prevRoute,
    submitFailed,
    subtitle,
    title,
    validateOnBlur,
    validateOnChange,
    validationSchema,
  } = props;

  return (
    <Formik
      initialTouched={initialTouched}
      initialValues={initialValues}
      validateOnBlur={validateOnBlur ?? true}
      validateOnChange={validateOnChange ?? true}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Form className={sharedClasses.form}>
          <GridLayout isNarrowView={isNarrowView}>
            <Header isNarrowView={isNarrowView}>
              <Grid container alignItems="center">
                <LogoImage width={36} height={36} fill={Colors.Black} />
                &emsp;&emsp;
                {(typeof header === "function" ? header(formik.values) : header) || "\xa0"}
              </Grid>
            </Header>
            {(submitFailed || errorMessage) && (
              <Row isNarrowView={isNarrowView}>
                <AlertMessage error message={errorMessage || "An unexpected error has occurred. Please try again later."} />
              </Row>
            )}
            {!!HeaderIcon && (
              <Row isNarrowView={isNarrowView} className={classes.image}>
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <HeaderIcon />
                </Box>
              </Row>
            )}
            <Body isNarrowView={isNarrowView}>
              {title && <TitleText hideMarginBottom={!!subtitle}>{typeof title === "function" ? title(formik.values) : title}</TitleText>}
              {subtitle && <SubtitleText>{typeof subtitle === "function" ? subtitle(formik.values) : subtitle}</SubtitleText>}
              {typeof children === "function" ? children(formik) : children}
            </Body>
            <Actions isNarrowView={isNarrowView} className={(hidePrev && hideNext && classes.hideFooter) || ""}>
              {!hidePrev && prevRoute && (
                <Button variant="contained" disabled={disablePrev || isLoading || isSubmitting} onClick={() => prevRoute(formik.values)} tabIndex="-1">
                  Previous
                </Button>
              )}
              {!hideNext && (
                <Button
                  color="primary"
                  variant="contained"
                  type={!!nextRouteHref ? undefined : "submit"}
                  href={nextRouteHref ? nextRouteHref : undefined}
                  className={classes.next}
                  disabled={disableNext || isLoading || isSubmitting}
                  key={`${disableNext}Next`}
                >
                  {nextLabel ?? "Next"}
                </Button>
              )}
            </Actions>
          </GridLayout>
          <ErrorFocus />
        </Form>
      )}
    </Formik>
  );
};

const useStyles = makeStyles(
  {
    hideFooter: {
      minHeight: "0px !important",
      padding: "0 !important",
    },
    image: {
      height: 264,
      "@media(min-width: 768px)": {
        height: 288,
      },
      "@media(max-width: 767px)": {
        borderRadius: "20px 20px 0 0",
      },
    },
    next: {
      marginLeft: "auto",
    },
  },
  { name: "ScaffoldAppScreen" }
);

export default ScaffoldAppScreen;
